<script setup lang="ts">
import {
  Is24Layout,
  Is24MainNavItem,
  Is24SideMenuNavItem,
  type MenuItem,
} from '@smg-real-estate/domus';
import { FavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { initSplitStoreFromSession, type SmgSplitSession } from '@smg-real-estate/vue-experiments';
import { computed, onMounted, onServerPrefetch, ref, useSSRContext } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { useAuth0Client } from '@/composables/useAuth0Client';
import { getAuthToken } from '@/helpers/Auth/authClient';
import { getAppMetaInfo } from '@/helpers/SEO/metaInfoHelper';
import { useGetLanguageSwitchLink, useLocale } from '@/router/routeMeta';
import { SplitFeatureFlags } from '@/splitExperiments/splitFeatureFlags';
import { trackFavouritesNavigation } from '@/tracking/tracking';
import { useHead } from '@unhead/vue';

const { t, tm } = useI18n();
const locale = useLocale();
const route = useRoute();
const store = useStore();
const baseUrl = import.meta.env.VUE_APP_BASE_URL;
const getLanguageSwitchLink = useGetLanguageSwitchLink();
const authToken = ref<string | undefined>();
const auth0Client = useAuth0Client();
const ssrContext = useSSRContext<{ smgSplitSession?: SmgSplitSession }>();

useHead(() => {
  return getAppMetaInfo(locale.value);
});

const isNewNavigationEnabled = computed<boolean>(() =>
  store.getters['split/isTreatmentOn'](SplitFeatureFlags.DD4533_NEW_TOP_LEVEL_NAVIGATION),
);
const isUserLoggedIn = computed(() => !!authToken.value);

const userAccountMenuItem = computed(() => ({
  label: t('$is24.nav.harmonize.overviewMenuItem.label'),
  href: `${import.meta.env.VUE_APP_BASE_URL}/${locale.value}/account-overview`,
}));
const mainHeaderMenuItem = computed((): MenuItem => {
  if (isUserLoggedIn.value) {
    return {
      label: tm('$is24.nav.loggedInMenuItem.label') as string,
      children: tm('$is24.nav.harmonize.privateItems'),
    } as unknown as MenuItem;
  }

  return userAccountMenuItem.value as unknown as MenuItem;
});
const adMenuItem = computed(
  () =>
    tm(
      isNewNavigationEnabled.value
        ? '$is24.nav.topNavNewListingMenuItem'
        : '$is24.nav.harmonize.newListingMenuItem',
    ) as unknown as MenuItem,
);
const favouriteMenuItem = computed(() => tm('$is24.nav.favoriteMenuItem') as unknown as MenuItem);

onMounted(async () => {
  authToken.value = await getAuthToken(auth0Client);
});

onServerPrefetch(() => {
  if (ssrContext?.smgSplitSession) {
    initSplitStoreFromSession(store, ssrContext.smgSplitSession);
  }
});

const handleFavouritesNavigation = (): void => {
  trackFavouritesNavigation(locale.value);
};
</script>

<template>
  <Is24Layout
    id="app"
    :baseUrl="baseUrl"
    :collapsibleStartUnfolded="false"
    :currentLanguage="locale"
    :currentRoute="route"
    :getLanguageToLink="getLanguageSwitchLink"
    :isWhiteLabelSolution="false"
    :hideMainNavigation="false"
    :isTopNavEnabled="isNewNavigationEnabled"
    :isUserLoggedIn="isUserLoggedIn"
  >
    <template #headerMetaAction>
      <Is24MainNavItem
        :menuItem="adMenuItem"
        :currentRoute="route"
        :baseUrl="baseUrl"
        :bordered="true"
      />
    </template>
    <template #headerMetaNavigation>
      <FavouritesLink
        :baseUrl="baseUrl"
        :menuItem="favouriteMenuItem"
        :menuNavItem="Is24MainNavItem"
        :route="route"
        @click="handleFavouritesNavigation"
      />
      <Is24MainNavItem
        :menuItem="mainHeaderMenuItem"
        icon="PersonOutline"
        :baseUrl="baseUrl"
        :currentRoute="route"
        dropdownOrientation="right"
      />
    </template>
    <template #sideMenuMetaNavigation>
      <FavouritesLink
        :baseUrl="baseUrl"
        :menuItem="favouriteMenuItem"
        :menuNavItem="Is24SideMenuNavItem"
        :route="route"
        @click="handleFavouritesNavigation"
      />
    </template>
    <template #sideMenuAccountNavigation>
      <template v-if="isUserLoggedIn">
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.overviewMenuItem') as unknown as MenuItem"
          icon="PersonOutline"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.settingsMenuItem') as unknown as MenuItem"
          icon="Settings"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.myAdsMenuItem') as unknown as MenuItem"
          icon="Appointment"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.myRealEstateMenuItem') as unknown as MenuItem"
          icon="House"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.searchAlertMenuItem') as unknown as MenuItem"
          icon="Bell"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.tenantPlusMenuItem') as unknown as MenuItem"
          icon="PlusMembership"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.signOutMenuItem') as unknown as MenuItem"
          icon="SignOut"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
      </template>
    </template>
    <router-view />
  </Is24Layout>
</template>

<style lang="scss">
@use './assets/globals-is24';
</style>
